import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NgZorroModule } from '../../../shared/modules/ng-zorro/ng-zorro.module';
import { ButtonComponent, PopupDialogComponent } from '@ppl/ui';
import { ListTableComponent } from '@ppl/ui';
import {
  TableRowAction,
  TableRowActionsModel,
  ColumnType,
} from '../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../shared/interfaces/actions';
import { DateTimeFormatterService } from '../../../shared/utils/date-time-formatter.service';
import {
  SearchProvidersDetailsItem,
  SearchProvidersPayload,
  SearchProvidersResponse,
} from '../../../shared/interfaces/provider.model';
import { LeadDetailsService } from '../../../services/lead-details.service';
import { ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS, STATIC_DATA } from '../../../shared/constants/static-data.constants';
import { SectionsService } from '../../../services/sections.service';
import { UserService } from '../../../services/user.service';
import { StorageService } from '../../../services/storage.service';
import { DatepickerComponent } from '@ppl/ui';
import { MessageModule } from 'primeng/message';
import { LookupValueItem } from 'src/app/interfaces/lookup.interface';
import { BaseComponent } from 'src/app/shared/utils/base.component';

@Component({
  selector: 'ppl-provider-search-and-select',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzDatePickerModule,
    NgZorroModule,
    ButtonComponent,
    ListTableComponent,
    DatepickerComponent,
    MessageModule,
    PopupDialogComponent,
    FormsModule,
    NzAlertModule
  ],
  templateUrl: './provider-search-and-select.component.html',
  styleUrl: './provider-search-and-select.component.css',
})
export class ProviderSearchAndSelectComponent extends BaseComponent implements OnInit {
  private dateTimeFormatterSvc = inject(DateTimeFormatterService);
  private leadDetailsSvc = inject(LeadDetailsService);
  @Input() providerType!: string;
  @Input() allAssociations: any = null;
  /** Relationship lookup codes that cannot be selected for this association. */
  @Input() disabledRelationshipCodes: string[] = [];
  @Output() cancel = new EventEmitter<void>();
  @Output() providerSelected = new EventEmitter<SearchProvidersDetailsItem>();
  @Output() addingNewProvider = new EventEmitter<void>();
  relationshipOptions: LookupValueItem[] = [];
  providers: WritableSignal<SearchProvidersDetailsItem[] | null> = signal(null);
  selectedRelationship: string = '';
  selectedProvider: any = null;
  showRelationShipDialog: boolean = false;
  showRelationshipError: boolean = false;
  associationError: string = '';
  showResultsList: Signal<boolean> = computed(() => {
    return this.providers() === null ? false : this.providers()?.length != 0;
  });
  program: any;

  searchQueryFormGroup = new FormGroup({
    fname: new FormControl(''),
    lname: new FormControl(''),
    dob: new FormControl<Date | string>(''),
    pplId: new FormControl(''),
    providerType: new FormControl(''),
    selectedProgramCode: new FormControl(''),
    ssn: new FormControl('')
  },
    { validators: this.pplIdOrSsnValidator() });

  actionButtons: TableRowActionsModel = new TableRowActions({ add: true });

  cols: ColumnType[] = [
    {
      header: 'PPL ID',
      field: 'providerPPLId',
    },
    {
      header: 'First Name',
      field: 'firstName',
    },
    {
      header: 'Last Name',
      field: 'lastName',
    },
    {
      header: 'Date of Birth',
      field: 'dob',
    },
  ];

  constructor(private sectionsService: SectionsService, private userService: UserService, private storageService: StorageService) {
    super();
  }

  ngOnInit(): void {
    this.program = this.userService.getSelectedProgram();
    this.relationshipOptions = this.getLookup(
      this.providerType === ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS['employer']
        ? 'Employment_Tasks'
        : 'Relationship'
    );
    this.initForm();
  }

  pplIdOrSsnValidator(): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const pplId = control.get('pplId')?.value;
      const ssn = control.get('ssn')?.value;

      return (pplId || ssn) ? null : { pplIdOrSsnOrDobRequired: true };
    };
  }

  initForm() {
    if (this.providerType) {
      this.searchQueryFormGroup.controls['providerType'].setValue(
        this.providerType
      );
    }
    if (this.program && this.program.program_code) {
      this.searchQueryFormGroup.controls['selectedProgramCode'].setValue(
        this.program.program_code
      );
    }
  }

  action(event: any) {
    if ((event.action = TableRowAction.Add)) {
      this.selectedProvider = event.data;
      this.associationError = '';

      const validationError = this.validateAssociationRules(this.selectedProvider);
      if (validationError) {
        this.associationError = validationError;
        return;
      }

      const enrollmentTypes = this.sectionsService.getEnrollmentType();
      // if enrollment type is equal to provider or employer or independent facilitator or independent contractor then show relationship dialog to select relationship with consumer
      if (enrollmentTypes && enrollmentTypes.length > 0) {
        const fieldName = enrollmentTypes[0];
        if (fieldName === 'provider' || fieldName === 'employer' || fieldName === 'independentFacilitator' || fieldName === 'contractor') {
          this.showRelationShipDialog = true;
        } else {
          this.handleAddProvider(this.selectedProvider);
        }
      }
    }
  }

  limitDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 9) {
      value = value.slice(0, 9);
    }

    event.target.value = value;
    this.searchQueryFormGroup.get('ssn')?.setValue(value);
  }

  handleAddProvider(provider: any) {
    // this.providerSelected.emit(provider);
    const enrollmentTypes = this.sectionsService.getEnrollmentType();
    const fieldName = enrollmentTypes && enrollmentTypes.length > 0 ? enrollmentTypes[0] : "provider";
    const id = this.storageService.getParticipantId() || this.userService.getSelectedLead();
    const payload = {
      programCode: provider.programCode,
      providerId: provider.providerId,
      participantId: id,
      pageType: 'participantDetail',
      providerType: ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS[fieldName],
      relationship: this.selectedRelationship
    };
    this.leadDetailsSvc
      .addParticipantProviderAssociation(payload, true)
      .subscribe((data: any) => {
        if (data.status === 200) {
          this.associationError = '';
          this.cancel.emit();
        } else {
          this.associationError = data?.responsemsg?.[0]?.desc ?? data?.responsedata ?? 'An error occurred';
        }
      });
  }

  search() {
    this.associationError = '';
    if (this.searchQueryFormGroup.invalid) {
      // If the form is invalid, do not proceed with the search
      return;
    }
    const formGroupValue = { ...this.searchQueryFormGroup.value };
    const dob = formGroupValue.dob;
    const query = {
      firstName: formGroupValue.fname?.trim(),
      lastName: formGroupValue.lname?.trim(),
      providerPPLId: formGroupValue.pplId?.trim(),
      dob: dob
        ? this.dateTimeFormatterSvc.formatDateToUSDateString(dob as Date)
        : '',
      providerType: formGroupValue.providerType,
      programCode: formGroupValue.selectedProgramCode,
      ssn: formGroupValue.ssn?.trim()
    } as SearchProvidersPayload;
    this.leadDetailsSvc.searchProvidersByQuery(query).subscribe({
      next: (res: any) => {
        const searchRes = res as SearchProvidersResponse;
        if (
          !searchRes.responsedata ||
          searchRes.responsedata.providerDetails.length === 0
        ) {
          this.providers.set([]);
          return;
        }
        this.providers.set([
          ...(searchRes?.responsedata?.providerDetails ?? null),
        ]);
      },
    });
  }

  handleOk() {
    if (this.selectedRelationship) {
      if (this.selectedProvider) {
        this.handleAddProvider(this.selectedProvider);
        this.handleCancel();
      }
    } else {
      // show error message to select relationship
      this.showRelationshipError = true;
    }
  }

  /**
   * Validates association rules before adding a provider.
   *
   * POA rules:
   *   - POA + EOR cannot be a provider for the same participant
   *   - POA cannot also be a Legal Guardian
   *
   * Conservator rules:
   *   - Conservator + AR cannot be a provider for the same participant
   *   - Conservator + EOR cannot be a provider for the same participant
   *   - Conservator cannot also be a Legal Guardian
   *
   * Legal Guardian rules:
   *   - Legal Guardian cannot be a provider for the same participant
   */
  private validateAssociationRules(provider: any): string | null {
    if (!this.allAssociations || !provider) return null;

    const providerId = provider.providerId;
    const addingAs = this.providerType;
    // All provider role codes: Independent Provider, Independent Contractor, Vendor,
    // Independent Facilitator. IF must be included or the provider-overlap rules
    // (e.g. Conservator+AR cannot be a provider) silently skip facilitator assignments.
    const providerTypes = ['IR', 'IC', 'VE', 'IF'];
    const isAddingAsProvider = providerTypes.includes(addingAs);

    const existsInType = (type: string): boolean => {
      const list = this.allAssociations[type] || [];
      return list.some((a: any) => a.providerId === providerId && a.isActive);
    };

    const isPOA = existsInType('POA');
    const isEMP = existsInType('EMP');
    const isAR = existsInType('AR');
    const isLG = existsInType('LG');
    const isCNV = existsInType('CNV');
    const isProvider = providerTypes.some(t => existsInType(t));

    // --- POA rules (scoped to this participant) ---
    // POA + EOR cannot be a provider for the same participant
    if (isAddingAsProvider && isPOA && isEMP) {
      return 'This individual is both POA and Employer of Record for this participant. They cannot be added as a provider.';
    }
    if (addingAs === 'EMP' && isPOA && isProvider) {
      return 'This individual is a POA and already a provider for this participant. Adding as Employer of Record would violate association rules.';
    }
    if (addingAs === 'POA' && isEMP && isProvider) {
      return 'This individual is an Employer of Record and already a provider for this participant. Adding as POA would violate association rules.';
    }

    // POA and Legal Guardian are mutually exclusive for the same participant
    if (addingAs === 'POA' && isLG) {
      return 'This individual is already a Legal Guardian for this participant. A POA cannot also be a Legal Guardian for the same participant.';
    }
    if (addingAs === 'LG' && isPOA) {
      return 'This individual is already a POA for this participant. A Legal Guardian cannot also be a POA for the same participant.';
    }

    // --- Conservator rules ---
    // Conservator + AR cannot be a provider
    if (isAddingAsProvider && isCNV && isAR) {
      return 'This individual is both Conservator and Authorized Representative. They cannot be added as a provider.';
    }
    if (addingAs === 'AR' && isCNV && isProvider) {
      return 'This individual is a Conservator and already a provider. Adding as Authorized Representative would violate association rules.';
    }
    if (addingAs === 'CNV' && isAR && isProvider) {
      return 'This individual is an Authorized Representative and already a provider. Adding as Conservator would violate association rules.';
    }

    // Conservator + EOR cannot be a provider
    if (isAddingAsProvider && isCNV && isEMP) {
      return 'This individual is both Conservator and Employer of Record. They cannot be added as a provider.';
    }
    if (addingAs === 'EMP' && isCNV && isProvider) {
      return 'This individual is a Conservator and already a provider. Adding as Employer of Record would violate association rules.';
    }
    if (addingAs === 'CNV' && isEMP && isProvider) {
      return 'This individual is an Employer of Record and already a provider. Adding as Conservator would violate association rules.';
    }

    // Conservator and Legal Guardian are mutually exclusive
    if (addingAs === 'CNV' && isLG) {
      return 'This individual is already a Legal Guardian. A Conservator cannot also be a Legal Guardian.';
    }
    if (addingAs === 'LG' && isCNV) {
      return 'This individual is already a Conservator. A Legal Guardian cannot also be a Conservator.';
    }

    // --- Legal Guardian rules ---
    // Legal Guardian cannot be a provider for the same participant
    if (isAddingAsProvider && isLG) {
      return 'This individual is a Legal Guardian for this participant. They cannot be added as a provider.';
    }
    if (addingAs === 'LG' && isProvider) {
      return 'This individual is already a provider for this participant. They cannot be added as a Legal Guardian.';
    }

    return null;
  }

  handleCancel() {
    this.showRelationShipDialog = false;
    this.selectedRelationship = '';
    this.selectedProvider = null;
    this.showRelationshipError = false;
    this.associationError = '';
  }
}
